/**
 * Session Validator Hook
 *
 * Monitors session expiry and refreshes token when needed.
 * Does NOT redirect on missing session - the AuthContext handles that.
 *
 * This hook only handles:
 * 1. Refreshing sessions that are about to expire
 * 2. Logging session-related events for debugging
 *
 * NOTE: We don't use getSession() for validation because it has race conditions
 * with the auth state listener. The AuthContext is the source of truth.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';
import { useAuth } from '@/contexts/AuthContext';

export function useSessionValidator() {
  const location = useLocation();
  const { session } = useAuth();

  useEffect(() => {
    // Don't do anything on auth pages
    if (location.pathname === '/auth' || location.pathname.startsWith('/auth/')) {
      return;
    }

    // Only check session expiry if we have a session
    if (!session) {
      return;
    }

    const checkAndRefreshSession = async () => {
      try {
        const expiresAt = session.expires_at;
        if (expiresAt) {
          const now = Math.floor(Date.now() / 1000);
          const timeUntilExpiry = expiresAt - now;

          // If less than 5 minutes until expiry, try to refresh
          if (timeUntilExpiry < 300 && timeUntilExpiry > 0) {
            logger.debug('Session expiring soon, refreshing...', { timeUntilExpiry });
            const { error: refreshError } = await supabase.auth.refreshSession();
            if (refreshError) {
              logger.error('Session refresh failed', refreshError);
              // Don't sign out - let the session expire naturally and AuthContext will handle it
            } else {
              logger.debug('Session refreshed successfully');
            }
          }
        }
      } catch (err) {
        logger.error('Session check error', err);
        // Don't do anything drastic - AuthContext will handle auth state changes
      }
    };

    checkAndRefreshSession();
  }, [location.pathname, session]);
}
